import { ROUTE } from '../../routes';

import { videoRouteFromPath, viewUnder, type VideoTabRoutes, type VideoView } from './video-routes';

/**
 * Deep links of the Marketing Assets tabs that are not video tabs: Image Banners (`/banners`, the banner kits), HTML5
 * Banners (`/html5-banners`), Mini-games (`/mini-games`) and Landing Pages (`/landing-pages`). Same library / new / edit shape as the Video Ads tabs (see `video-routes.ts`), so the view type
 * and the tab route builders are shared. Pure (no react-router import), unit-testable in the node jest env.
 */

/** The view an image banner, HTML5 banner or mini-game URL names: the library grid, a "create new", or one saved
 *  entity. */
export type AdView = VideoView;

/** The Marketing Assets tabs these deep links belong to. */
export type AdTab = 'banner-kits' | 'html5-banners' | 'playables' | 'landing-pages';

/** An ad URL resolved: which tab it belongs to and the view within it. */
export type AdRoute = { tab: AdTab; view: AdView };

const TAB_ROUTES: Record<AdTab, VideoTabRoutes> = {
  'banner-kits': {
    library: ROUTE.creativesBannerKits,
    create: ROUTE.creativesBannerKitNew,
    edit: ROUTE.creativesBannerKit,
  },
  'html5-banners': {
    library: ROUTE.creativesHtml5Banners,
    create: ROUTE.creativesHtml5BannerNew,
    edit: ROUTE.creativesHtml5Banner,
  },
  playables: {
    library: ROUTE.creativesPlayables,
    create: ROUTE.creativesPlayableNew,
    edit: ROUTE.creativesPlayable,
  },
  'landing-pages': {
    library: ROUTE.creativesLandingPages,
    create: ROUTE.creativesLandingPageNew,
    edit: ROUTE.creativesLandingPage,
  },
};

/** The ad tabs in the order their URLs are matched (their bases do not prefix each other). */
const AD_TABS: readonly AdTab[] = ['banner-kits', 'html5-banners', 'playables', 'landing-pages'];

/** The route builders of one ad tab, so its library navigates within the tab without branching per call. */
export const adTabRoutes = (tab: AdTab): VideoTabRoutes => TAB_ROUTES[tab];

/** Parse a pathname into its {@link AdRoute} (tab + view), or null when it is not an ad tab URL. */
export const adRouteFromPath = (pathname: string): AdRoute | null => {
  const path = pathname.replace(/\/+$/, '');
  for (const tab of AD_TABS) {
    const view = viewUnder(TAB_ROUTES[tab].library(), path);
    if (view) return { tab, view };
  }
  return null;
};

/** The {@link AdView} of an ad tab URL, or null when it is not one. For callers that only ask
 *  "is this an ad library / editor page", e.g. the page transition. */
export const adViewFromPath = (pathname: string): AdView | null => adRouteFromPath(pathname)?.view ?? null;

/**
 * The key React Router's `ScrollRestoration` files a location's window scroll under (its `getKey`). An ad library and
 * its editor (`/videos` and `/videos/:id` or `/videos/new`; the same for image banners, HTML5 banners and mini-games)
 * are one page, the editor a sheet over the grid (see PageTransition), so both share their tab's key: opening or
 * closing an ad restores the scroll the page already has instead of resetting it to the top (on a phone the document
 * is the scroller, so the reset scrolled the grid behind the sheet). Any other location keeps its own history entry's
 * key, the router's default. Pure.
 */
export const scrollKeyOf = (location: { key: string; pathname: string }): string => {
  const video = videoRouteFromPath(location.pathname);
  if (video) return `ad-library:${video.pipeline}`;
  const ad = adRouteFromPath(location.pathname);
  return ad ? `ad-library:${ad.tab}` : location.key;
};
